import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plate } from "@/components/Plate";
import { toast } from "sonner";
import { Trash, Pencil } from "@phosphor-icons/react";

const ROLE_LABELS = {
  super_admin: "Süperadmin", admin: "Admin", operator: "Operatör",
  employee: "Çalışan", customer: "Müşteri", business: "İşletme",
};

export default function Users() {
  const emptyForm = { name: "", email: "", password: "", role: "operator", phone: "", plate: "", corporate_account_id: "" };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === "super_admin";
  const { data: agreementsData } = useLiveQuery(
    TAGS.CORPORATE,
    async () => (await api.get("/business-agreements")).data,
    { intervalMs: 0 },
  );
  const agreements = agreementsData || [];

  const { data, refetch } = useLiveQuery(
    TAGS.USERS,
    async () => (await api.get("/users")).data,
  );
  const users = data || [];

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "operator",
      phone: u.phone || "",
      plate: u.plate || "",
      corporate_account_id: u.corporate_account_id || "",
    });
    setOpen(true);
  };

  const startCreate = () => {
    setEditing(null); setForm(emptyForm); setOpen(true);
  };

  const submit = async () => {
    try {
      if (editing) {
        const payload = {
          name: form.name || null,
          phone: form.phone || null,
          plate: form.plate || null,
          role: form.role || null,
          corporate_account_id: form.role === "business" ? (form.corporate_account_id || null) : null,
        };
        // Süperadmin ise email + şifre de gönderilebilir
        if (isSuperAdmin) {
          if (form.email && form.email !== editing.email) payload.email = form.email;
          if (form.password && form.password.trim()) payload.password = form.password.trim();
        }
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Kullanıcı güncellendi");
      } else {
        await api.post("/users", {
          ...form, plate: form.plate || null, phone: form.phone || null,
          corporate_account_id: form.role === "business" ? (form.corporate_account_id || null) : null,
        });
        toast.success("Kullanıcı oluşturuldu");
      }
      setOpen(false); setEditing(null); setForm(emptyForm);
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kaydedilemedi"); }
  };

  const del = async (u) => {
    if (!window.confirm(`${u.name} (${u.email}) silinsin mi?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Kullanıcı silindi");
      refetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Yönetim</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Kullanıcılar</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild>
            <Button data-testid="user-add-open" onClick={startCreate} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase font-semibold tracking-widest">Yeni Kullanıcı</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Kullanıcı Düzenle" : "Yeni Kullanıcı"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Ad Soyad</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="user-name-input" /></div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="rounded-sm"
                       disabled={!!editing && !isSuperAdmin}
                       data-testid="user-email-input" />
                {editing && !isSuperAdmin && <div className="text-[10px] text-muted-foreground mt-1">Email değiştirilemez</div>}
                {editing && isSuperAdmin && <div className="text-[10px] text-muted-foreground mt-1">Süperadmin: email değiştirilebilir</div>}
              </div>
              {!editing && (
                <div>
                  <Label>Şifre</Label>
                  <Input type="password" value={form.password}
                         onChange={(e) => setForm({ ...form, password: e.target.value })}
                         className="rounded-sm" data-testid="user-password-input" />
                </div>
              )}
              {editing && isSuperAdmin && (
                <div>
                  <Label>Yeni Şifre (opsiyonel)</Label>
                  <Input type="password" value={form.password}
                         placeholder="Değiştirmek istemiyorsanız boş bırakın"
                         onChange={(e) => setForm({ ...form, password: e.target.value })}
                         className="rounded-sm" data-testid="user-password-input" />
                  <div className="text-[10px] text-muted-foreground mt-1">En az 4 karakter. Boş bırakırsanız şifre değişmez.</div>
                </div>
              )}
              <div>
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-sm" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="super_admin">Süperadmin</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="operator">Operatör</SelectItem>
                    <SelectItem value="employee">Çalışan</SelectItem>
                    <SelectItem value="business">İşletme (Panel)</SelectItem>
                    <SelectItem value="customer">Müşteri</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "business" && (
                <div>
                  <Label>Bağlı İşletme Anlaşması</Label>
                  <Select value={form.corporate_account_id || ""} onValueChange={(v) => setForm({ ...form, corporate_account_id: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="user-business-select"><SelectValue placeholder="İşletme seçin" /></SelectTrigger>
                    <SelectContent>
                      {agreements.map((a) => (
                        <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-muted-foreground mt-1">Bu kullanıcı giriş yapınca yalnızca kendi işletme panelini görür.</div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm" data-testid="user-phone-input" /></div>
                <div><Label>Plaka</Label><Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} className="rounded-sm font-plate uppercase" data-testid="user-plate-input" /></div>
              </div>
              <Button data-testid="user-save-btn" onClick={submit} className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 uppercase font-semibold tracking-widest">
                {editing ? "Güncelle" : "Oluştur"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-sm border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Telefon</TableHead>
                <TableHead>Plaka</TableHead>
                <TableHead className="text-right">İşlem</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">
                    {u.email}
                    {u.role === "super_admin" && (
                      <Badge variant="outline" className="ml-2 rounded-sm text-[9px] uppercase tracking-widest border-primary/40 text-primary">
                        Süperadmin
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="rounded-sm uppercase text-[10px] tracking-widest">
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.phone || "—"}</TableCell>
                  <TableCell>{u.plate ? <Plate>{u.plate}</Plate> : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(u)}
                              data-testid={`user-edit-${u.email}`}
                              title="Düzenle">
                        <Pencil size={14} />
                      </Button>
                      {u.role !== "super_admin" && (
                        <Button size="sm" variant="ghost" onClick={() => del(u)}
                                data-testid={`user-delete-${u.email}`}
                                title="Sil"
                                className="text-destructive hover:text-destructive">
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
